import React, { useState, useMemo } from 'react';
import { DollarSign, Copy, Check } from 'lucide-react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const InterestCalculator = () => {
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [period, setPeriod] = useState('');
  const [periodUnit, setPeriodUnit] = useState('years');
  const [interestType, setInterestType] = useState('simple');
  const [compoundFrequency, setCompoundFrequency] = useState('annually');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    const p = parseFloat(principal);
    const r = parseFloat(rate) / 100;
    const t = parseFloat(period);
    if (!p || !r || !t || p <= 0 || r <= 0 || t <= 0) return null;

    // Convert period to years
    let years;
    if (periodUnit === 'days') years = t / 365;
    else if (periodUnit === 'months') years = t / 12;
    else years = t;

    let interest, total;
    if (interestType === 'simple') {
      interest = p * r * years;
      total = p + interest;
    } else {
      // Compound interest
      let n;
      switch (compoundFrequency) {
        case 'daily': n = 365; break;
        case 'monthly': n = 12; break;
        case 'quarterly': n = 4; break;
        default: n = 1;
      }
      total = p * Math.pow(1 + r / n, n * years);
      interest = total - p;
    }

    return { interest, total, principal: p };
  }, [principal, rate, period, periodUnit, interestType, compoundFrequency]);

  const handleCopy = () => {
    if (result) {
      const text = `Principal: ${formatCurrency(result.principal)}\nInterest: ${formatCurrency(result.interest)}\nTotal: ${formatCurrency(result.total)}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Principal Amount ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            placeholder="e.g. 10000"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Annual Interest Rate (%)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="e.g. 5.25"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Period</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="1"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="e.g. 2"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
            />
            <select
              value={periodUnit}
              onChange={(e) => setPeriodUnit(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
            >
              <option value="days">Days</option>
              <option value="months">Months</option>
              <option value="years">Years</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Interest Type</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setInterestType('simple')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                interestType === 'simple'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Simple
            </button>
            <button
              onClick={() => setInterestType('compound')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                interestType === 'compound'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Compound
            </button>
          </div>
        </div>
      </div>

      {interestType === 'compound' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Compounding Frequency</label>
          <select
            value={compoundFrequency}
            onChange={(e) => setCompoundFrequency(e.target.value)}
            className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
      )}

      {result && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Interest Earned</p>
                <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-yellow-600" />
                  {formatCurrency(result.interest)}
                </p>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="text-sm text-gray-600">Principal</p>
                  <p className="text-lg font-semibold text-gray-700">{formatCurrency(result.principal)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-lg font-semibold text-gray-700">{formatCurrency(result.total)}</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleCopy}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Copy results"
            >
              {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestCalculator;
